import React, { useEffect, useRef } from 'react';
/**
 * CSS selector string for possible focusable elements.
 * Includes buttons, links, inputs, selects, textareas, elements with tabindex,
 * and contenteditable elements, excludes disabled elements.
 */
const FOCUSABLE_ELEMENTS = [
    'button:not(:disabled)',
    'a[href]',
    'input:not(:disabled)',
    'select:not(:disabled)',
    'textarea:not(:disabled)',
    '[tabindex]:not([tabindex="-1"]):not(:disabled)',
    '[contenteditable="true"]',
].join(', ');
/**
 * A React component that traps focus within its children when active.
 * Prevents keyboard navigation from leaving the trapped area and handles
 * dynamic DOM changes to maintain focus within the container.
 */
export const FocusTrap = ({ children, isActive }) => {
    const containerRef = useRef(null);
    const previousActiveElementRef = useRef(null);
    const lastFocusedIndexRef = useRef(-1);
    const getFocusableElements = () => {
        if (!containerRef.current)
            return [];
        return Array.from(containerRef.current.querySelectorAll(FOCUSABLE_ELEMENTS));
    };
    const handleTabNavigation = (e, focusableElements) => {
        // No elements to navigate between
        if (focusableElements.length === 0)
            return;
        const firstElement = focusableElements.at(0);
        const lastElement = focusableElements.at(-1);
        // Shift+Tab on first element: focus on last element
        if (e.shiftKey && document.activeElement === firstElement) {
            lastElement?.focus();
            e.preventDefault();
        }
        // Tab on last element: focus on first element
        else if (!e.shiftKey && document.activeElement === lastElement) {
            firstElement?.focus();
            e.preventDefault();
        }
    };
    const restoreFocusAfterMutation = (newFocusableElements) => {
        // Only restore focus if it's been lost (moved to body or null)
        if (document.activeElement === document.body || !document.activeElement) {
            // Try to focus the element at the last known index, fallback to the last element
            const nextElement = newFocusableElements[lastFocusedIndexRef.current] ??
                newFocusableElements.at(-1);
            nextElement?.focus();
        }
    };
    useEffect(() => {
        if (!isActive)
            return;
        // Store the currently focused element to restore it when the trap deactivates
        previousActiveElementRef.current = document.activeElement;
        // Get initial list of focusable elements
        let focusableElements = getFocusableElements();
        const handleMutation = () => {
            const newFocusableElements = getFocusableElements();
            restoreFocusAfterMutation(newFocusableElements);
            focusableElements = newFocusableElements;
        };
        const handleFocusIn = (e) => {
            lastFocusedIndexRef.current = focusableElements.indexOf(e.target);
        };
        const handleKeyDown = (e) => {
            if (e.key === 'Enter') {
                // Use setTimeout delay the mutation handling after Enter key press to account for async DOM updates
                // and then update the focusable elements list after the DOM has settled
                setTimeout(() => handleMutation(), 0);
            }
            if (e.key !== 'Tab' || !containerRef.current)
                return;
            handleTabNavigation(e, focusableElements);
        };
        const mutationObserver = new MutationObserver(handleMutation);
        // Start observing the container for DOM changes (added/removed child elements)
        if (containerRef.current) {
            mutationObserver.observe(containerRef.current, {
                childList: true, // Watch for direct children being added/removed
                subtree: true, // Watch for changes in all descendants
            });
        }
        document.addEventListener('keydown', handleKeyDown);
        containerRef.current?.addEventListener('focusin', handleFocusIn);
        return () => {
            previousActiveElementRef.current?.focus();
            document.removeEventListener('keydown', handleKeyDown);
            containerRef.current?.removeEventListener('focusin', handleFocusIn);
            mutationObserver.disconnect();
        };
    }, [isActive]);
    return React.createElement("div", { ref: containerRef }, children);
};
